import { auth } from './config'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification as firebaseSendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  type OAuthCredential,
  type User,
} from 'firebase/auth'
import type { FirebaseError } from 'firebase/app'
import { getAuthErrorCode } from '@/utils/authErrors'
import { deriveDisplayName } from '@/utils/displayName'

/**
 * Proveedores de ingreso del portal: correo + contraseña y Google — los dos
 * únicos desde el 2026-10-03 (ADR-SEC-012). Contrato:
 * `specs/API-CONTRACT/firebase-auth.md`.
 */

export const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential'
const PROVIDER_ALREADY_LINKED_CODE = 'auth/provider-already-linked'

/** Vinculación pendiente: la credencial de Google que Firebase rechazó porque el correo ya tenía cuenta. */
export interface PendingGoogleLink {
  /** Correo de la cuenta existente (`error.customData.email`). `null` si Firebase no lo devolvió. */
  email: string | null
  credential: OAuthCredential
}

export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password)
  return userCredential.user
}

/** Scopes mínimos de Google (ADR-SEC-009). */
export const GOOGLE_SCOPES = ['profile', 'email'] as const

export const loginWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider()
  GOOGLE_SCOPES.forEach((scope) => provider.addScope(scope))
  const userCredential = await signInWithPopup(auth, provider)
  return userCredential.user
}

/**
 * Si `error` es el rechazo de Google porque el correo ya tiene cuenta con
 * contraseña, devuelve la credencial pendiente para vincularla (Op A.8).
 * No consulta `fetchSignInMethodsForEmail`: con email enumeration protection
 * devuelve vacío. No da por hecho `customData.email`: puede faltar.
 */
export const getPendingGoogleLink = (error: unknown): PendingGoogleLink | null => {
  if (getAuthErrorCode(error) !== ACCOUNT_EXISTS_CODE) return null
  const credential = GoogleAuthProvider.credentialFromError(error as FirebaseError)
  if (!credential) return null
  const customEmail = (error as { customData?: { email?: unknown } }).customData?.email
  return { email: typeof customEmail === 'string' && customEmail ? customEmail : null, credential }
}

/**
 * Vincula la credencial de Google a la cuenta de correo existente (Op A.8):
 * inicia sesión con la contraseña y luego `linkWithCredential`.
 *
 * Política del owner (ADR-SEC-012): NO se comprueba `emailVerified` de la
 * cuenta existente — las dos pruebas (sesión de Google + contraseña) bastan.
 */
export const linkGoogleToPasswordAccount = async (
  email: string,
  password: string,
  credential: OAuthCredential
): Promise<User> => {
  const { user } = await signInWithEmailAndPassword(auth, email, password)
  try {
    const linked = await linkWithCredential(user, credential)
    return linked.user
  } catch (err) {
    if (getAuthErrorCode(err) === PROVIDER_ALREADY_LINKED_CODE) return user
    throw err
  }
}

export const isNewUser = (user: User): boolean => {
  return user.metadata.creationTime === user.metadata.lastSignInTime
}

/**
 * Registro por correo (Op A.4). El nombre que recibe Auth es el **derivado**
 * (`deriveDisplayName`, 2026-11-02) — el mismo que `createUserDocument` escribe
 * en `users/{uid}`. Si `updateProfile` falla la cuenta ya existe, así que no se
 * aborta (antes se propagaba y dejaba la cuenta sin documento):
 * `createUserDocument` reintenta la sincronización después de escribir Firestore.
 */
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string
): Promise<User> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password)
  const user = userCredential.user
  try {
    await updateProfile(user, { displayName: deriveDisplayName(displayName, user.email ?? email) })
  } catch (err) {
    console.warn('[nombre] No se pudo escribir el nombre en Auth al registrar (se reintenta):', err)
  }
  return user
}

/**
 * Escribe `displayName` en el usuario de Auth de la sesión (2026-11-02). Se llama
 * SIEMPRE después de escribir `users/{uid}` (Firestore primero, Auth después):
 * la app móvil lee Auth para la presencia del aula y las alertas que ve el
 * profesor. `updateProfile` muta `auth.currentUser` en sitio. Lanza si no hay
 * sesión o si la sesión es de otro `uid`; el llamador decide si el fallo es
 * silencioso (alta de la cuenta) o visible (perfil).
 */
export const updateAuthDisplayName = async (uid: string, displayName: string): Promise<void> => {
  const current = auth.currentUser
  if (!current || current.uid !== uid) {
    throw new Error('No hay una sesión de Auth para el usuario que se actualiza')
  }
  await updateProfile(current, { displayName })
}

export const logout = async (): Promise<void> => {
  await signOut(auth)
}

export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email)
}

export const sendEmailVerification = async (user: User): Promise<void> => {
  await firebaseSendEmailVerification(user)
}

export const getCurrentUser = (): User | null => {
  return auth.currentUser
}
